import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDaybook, saveDaybook, exportDaybookToExcel } from "../../components/Api/GmsDaybook";
import { getEmployees, searchEmployees } from "../../components/Api/GmsEmployee";
import { getCustomers, searchCustomers } from "../../components/Api/GmsCustomer";
import { getSpaServices, searchSpaServices } from "../../components/Api/GmsSpaService";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import Select from "react-select";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Card, CardHeader, CardBody, Container, Row, Col } from "reactstrap";
import Swal from "sweetalert2";
import { Download, Search } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import 'bootstrap/dist/css/bootstrap.min.css';
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";
import CustomerAdvancedSearchModal from "../../components/CustomerAdvancedSearchModal";
import CustomerProfileModal from "../../components/CustomerProfileModal";

// Helper: format local date/time without timezone (like AddBooking)
const formatLocalDate = (date) => {
  if (!date) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const formatLocalDateTime = (date) => {
  if (!date) return null;
  const ymd = formatLocalDate(date);
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${ymd}T${hh}:${mm}:${ss}`;
};

const defaultForm = {
  daybook_Id: 0,
  business_Id: null,
  branch_Id: null,
  employee_Id: null,
  customer_Id: null,
  service_Id: null,
  transaction_DateTime: new Date().toISOString(),
  end_DateTime: null,
  cost: 0,
  discount_Percent: 0,
  discount_Amount: 0,
  notes: "",
  created_By: 1,
  created_Date: new Date().toISOString(),
};

const emptyFilter = {
  employee_Id: null,
  service_Id: null,
  business_Id: null,
  branch_Id: null,
  customer_Id: null,
  booking_Id: null,
  fromDate: null,
  toDate: null,
};

// Initialize with null dates to get all records
const getInitialFilter = () => {
  return {
    ...emptyFilter,
    fromDate: null,
    toDate: null
  };
};

export default function DaybookPage() {
  const navigate = useNavigate();
  const [daybooks, setDaybooks] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(getInitialFilter());

  // Search states for modal
  const [customerSearchOptions, setCustomerSearchOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);
  const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [serviceSearchOptions, setServiceSearchOptions] = useState([]);
  const [serviceSearchLoading, setServiceSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const [selectedEmployeeOption, setSelectedEmployeeOption] = useState(null);
  const [selectedServiceOption, setSelectedServiceOption] = useState(null);
  const [showAdvancedSearchModal, setShowAdvancedSearchModal] = useState(false);
  const [showCustomerProfile, setShowCustomerProfile] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);

  // Search states for filter
  const [filterCustomerSearchOptions, setFilterCustomerSearchOptions] = useState([]);
  const [filterCustomerSearchLoading, setFilterCustomerSearchLoading] = useState(false);
  const [filterEmployeeSearchOptions, setFilterEmployeeSearchOptions] = useState([]);
  const [filterEmployeeSearchLoading, setFilterEmployeeSearchLoading] = useState(false);
  const [filterServiceSearchOptions, setFilterServiceSearchOptions] = useState([]);
  const [filterServiceSearchLoading, setFilterServiceSearchLoading] = useState(false);
  const [filterSearchTimeout, setFilterSearchTimeout] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [totalNetCost, setTotalNetCost] = useState(0);

  // Date and time fields (like AddBooking)
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [startTime, setStartTime] = useState(new Date());
  const [stopTime, setStopTime] = useState(new Date());

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsDaybook');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsDaybook');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsDaybook');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsDaybook');
  const isAdmin = isUserAdmin();

  // Get current user's employee ID (same as BookingPage)
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentEmployeeId = currentUser.user_EmployeeId || currentUser.employeeId;


  // Determine if employee filter should be shown
  const showEmployeeFilter = !isViewOnly || isAdmin || (isViewOnly && !currentEmployeeId);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    const normalizeId = (value) => {
      if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
        return null;
      }
      const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
      return isNaN(numValue) || numValue <= 0 ? null : numValue;
    };

    let businessId, branchId;

    if (authUser && Object.keys(authUser).length > 0) {
      businessId = normalizeId(authUser.companyId);
      branchId = normalizeId(authUser.branchId);
    } else {
      businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
      branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
    }

    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
      businessId: businessId,
      branchId: branchId,
      companyId: businessId,
    };
  };

  const session = getUserSession();

  useEffect(() => {
    if (hasPermission) {
      loadBusinesses();
      loadAllBranches();
      loadAllEmployees();
      loadAllServices();
      loadDaybooks(filter, page);
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, page]);

  // Auto-populate company if user has companyId
  useEffect(() => {
    if (session.companyId && businesses.length > 0) {
      const companyId = typeof session.companyId === 'string' ? parseInt(session.companyId) : session.companyId;
      const companyExists = businesses.find(b => b.business_Id === companyId);
      if (companyExists && !form.business_Id) {
        setForm(prev => ({
          ...prev,
          business_Id: companyId
        }));
        loadBranches(companyId);
      }
    }
  }, [businesses, session.companyId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && form.business_Id) {
      const branchId = typeof session.branchId === 'string' ? parseInt(session.branchId) : session.branchId;
      const branchExists = branches.find(b => b.branch_Id === branchId && b.branch_Buisness_Id === form.business_Id);
      if (branchExists && !form.branch_Id) {
        setForm(prev => ({
          ...prev,
          branch_Id: branchId
        }));
      }
    }
  }, [branches, form.business_Id, session.branchId]);

  // Load employees when business/branch changes
  useEffect(() => {
    if (form.business_Id) {
      loadEmployees();
    }
  }, [form.business_Id, form.branch_Id]);

  // Load services when business changes
  useEffect(() => {
    if (form.business_Id) {
      loadServices();
    }
  }, [form.business_Id]);

  // Load customers
  useEffect(() => {
    loadCustomers();
  }, []);

  const loadBusinesses = async () => {
    try {
      const data = await getBusinesses({ business_Active: true });
      setBusinesses(data || []);
    } catch (error) {
      console.error("Error loading businesses:", error);
    }
  };

  const loadBranches = async (businessId = null) => {
    try {
      const filter = { branch_Active: true };
      if (businessId) {
        filter.Business_Id = businessId;
      }
      const data = await getBranches(filter);
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading branches:", error);
      setBranches([]);
    }
  };

  const loadAllBranches = async () => {
    try {
      const data = await getBranches({ branch_Active: true });
      setBranches(data || []);
    } catch (error) {
      console.error("Error loading all branches:", error);
      setBranches([]);
    }
  };

  const loadEmployees = async () => {
    try {
      const filter = {};
      if (form.business_Id) {
        filter.Employee_Business_Id = form.business_Id;
      }
      if (form.branch_Id && form.business_Id) {
        filter.Employee_Branch_Id = form.branch_Id;
      }
      // If no form selection, use session values
      if (!form.business_Id && session.businessId) {
        filter.Employee_Business_Id = session.businessId;
      }
      if (!form.branch_Id && session.branchId && session.businessId) {
        filter.Employee_Branch_Id = session.branchId;
      }
      const data = await getEmployees(filter);
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadAllEmployees = async () => {
    try {
      const data = await getEmployees({});
      setEmployees(data || []);
    } catch (error) {
      console.error("Error loading all employees:", error);
      setEmployees([]);
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await getCustomers({});
      setCustomers(data || []);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  const loadServices = async () => {
    try {
      const filter = { status: true };
      if (form.business_Id) {
        filter.Business_Id = form.business_Id;
      } else if (session.businessId) {
        filter.Business_Id = session.businessId;
      }
      const data = await getSpaServices(filter);
      const activeServices = (data || []).filter(service => service.status === true);
      setServices(activeServices);
      setFilteredServices(activeServices);
    } catch (error) {
      console.error("Error loading services:", error);
      setServices([]);
      setFilteredServices([]);
    }
  };

  const loadAllServices = async () => {
    try {
      const data = await getSpaServices({ status: true });
      const activeServices = (data || []).filter(service => service.status === true);
      setServices(activeServices);
      setFilteredServices(activeServices);
    } catch (error) {
      console.error("Error loading all services:", error);
      setServices([]);
      setFilteredServices([]);
    }
  };

  // Search functions for modal
  const searchCustomersManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setCustomerSearchOptions([]);
      return;
    }

    try {
      setCustomerSearchLoading(true);
      const cleanedInput = inputValue.replace(/\s+/g, '');
      const response = await searchCustomers(cleanedInput, 1, 20);
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      setCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers:', error);
      setCustomerSearchOptions([]);
    } finally {
      setCustomerSearchLoading(false);
    }
  };

  const searchEmployeesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setEmployeeSearchOptions([]);
      return;
    }

    try {
      setEmployeeSearchLoading(true);
      const businessId = form.business_Id || session.businessId;
      const branchId = form.branch_Id || session.branchId;
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);
      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: employee.employee_NameEn || 'N/A',
        employee: employee
      }));
      setEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees:', error);
      setEmployeeSearchOptions([]);
    } finally {
      setEmployeeSearchLoading(false);
    }
  };

  const searchServicesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setServiceSearchOptions([]);
      return;
    }

    try {
      setServiceSearchLoading(true);
      const response = await searchSpaServices(inputValue, 1, 20);
      // Filter only active services (status: true)
      const formattedOptions = response.items
        .filter(service => service.status === true)
        .map(service => ({
          value: service.spaService_Id,
          label: service.spaServiceName_En || service.SpaServiceName_En || 'N/A',
          service: service
        }));
      setServiceSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching services:', error);
      setServiceSearchOptions([]);
    } finally {
      setServiceSearchLoading(false);
    }
  };

  // Search functions for filter
  const searchFilterCustomersManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setFilterCustomerSearchOptions([]);
      return;
    }

    try {
      setFilterCustomerSearchLoading(true);
      const cleanedInput = inputValue.replace(/\s+/g, '');
      const response = await searchCustomers(cleanedInput, 1, 20);
      const formattedOptions = response.items.map(customer => ({
        value: customer.customer_Id,
        label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
        customer: customer
      }));
      setFilterCustomerSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching customers in filter:', error);
      setFilterCustomerSearchOptions([]);
    } finally {
      setFilterCustomerSearchLoading(false);
    }
  };

  const searchFilterEmployeesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setFilterEmployeeSearchOptions([]);
      return;
    }

    try {
      setFilterEmployeeSearchLoading(true);
      const businessId = filter.business_Id || session.businessId;
      const branchId = filter.branch_Id || session.branchId;
      const response = await searchEmployees(inputValue, 1, 20, businessId, branchId);
      const formattedOptions = response.items.map(employee => ({
        value: employee.employee_Id,
        label: employee.employee_NameEn || 'N/A',
        employee: employee
      }));
      setFilterEmployeeSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching employees in filter:', error);
      setFilterEmployeeSearchOptions([]);
    } finally {
      setFilterEmployeeSearchLoading(false);
    }
  };

  const searchFilterServicesManually = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
      setFilterServiceSearchOptions([]);
      return;
    }

    try {
      setFilterServiceSearchLoading(true);
      const response = await searchSpaServices(inputValue, 1, 20);
      // Filter only active services (status: true)
      const formattedOptions = response.items
        .filter(service => service.status === true)
        .map(service => ({
          value: service.spaService_Id,
          label: service.spaServiceName_En || service.SpaServiceName_En || 'N/A',
          service: service
        }));
      setFilterServiceSearchOptions(formattedOptions);
    } catch (error) {
      console.error('Error searching services in filter:', error);
      setFilterServiceSearchOptions([]);
    } finally {
      setFilterServiceSearchLoading(false);
    }
  };

  const loadDaybooks = async (filterData = filter, pageNumber = page) => {
    setLoading(true);
    try {
      // Clean filter - remove null values and convert to proper types
      const cleanFilter = {
        ...(filterData.employee_Id !== null && filterData.employee_Id !== undefined && { employee_Id: filterData.employee_Id }),
        ...(filterData.service_Id !== null && filterData.service_Id !== undefined && { service_Id: filterData.service_Id }),
        ...(filterData.business_Id !== null && filterData.business_Id !== undefined && { business_Id: filterData.business_Id }),
        ...(filterData.branch_Id !== null && filterData.branch_Id !== undefined && { branch_Id: filterData.branch_Id }),
        ...(filterData.customer_Id !== null && filterData.customer_Id !== undefined && { customer_Id: filterData.customer_Id }),
        ...(filterData.booking_Id !== null && filterData.booking_Id !== undefined && { booking_Id: filterData.booking_Id }),
        ...(filterData.fromDate && { fromDate: filterData.fromDate }),
        ...(filterData.toDate && { toDate: filterData.toDate })
      };

      // For users without edit permission, filter by their employee ID only (same as BookingPage)
      if (!hasEditOrHigher && currentEmployeeId) {
        cleanFilter.employee_Id = currentEmployeeId;
      }

      const data = await getDaybook(cleanFilter);
      // Sort by newest daybook_Id first (latest inserted record)
      const sortedData = data.sort((a, b) => b.daybook_Id - a.daybook_Id);
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = sortedData.slice(startIndex, endIndex);
      setDaybooks(paginatedData);
      setTotalCount(sortedData.length);
      // Calculate totals of all filtered records
      const totalCostValue = sortedData.reduce((total, d) => total + (d.cost || 0), 0);
      const totalDiscountValue = sortedData.reduce((total, d) => total + (d.discount_Amount || 0), 0);

      setTotalCost(totalCostValue);
      setTotalDiscount(totalDiscountValue);
      setTotalNetCost(totalCostValue - totalDiscountValue);

      console.log("✅ Daybook data processed - Count:", sortedData.length, "Total Cost:", totalCostValue, "Total Discount:", totalDiscountValue);
    } catch (error) {
      console.error("❌ Error loading daybooks:", error);
      Swal.fire("Error", "Failed to load daybooks: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'cost') {
        const cost = parseFloat(value) || 0;
        const percent = prev.discount_Percent || 0;
        const amount = (cost * (percent / 100));
        next.discount_Amount = parseFloat(amount.toFixed(2));
      }
      return next;
    });
  };

  const handleBusinessChange = (selectedOption) => {
    const businessId = selectedOption ? selectedOption.value : null;
    setForm({
      ...form,
      business_Id: businessId,
      branch_Id: null,
      employee_Id: null,
      service_Id: null,
      cost: 0,
    });
    if (businessId) {
      loadBranches(businessId);
      loadEmployees();
      loadServices();
    } else {
      setBranches([]);
      setEmployees([]);
      setServices([]);
      setFilteredServices([]);
    }
  };

  const handleBranchChange = (selectedOption) => {
    const branchId = selectedOption ? selectedOption.value : null;
    setForm({
      ...form,
      branch_Id: branchId,
      employee_Id: null,
    });
    if (branchId) {
      loadEmployees();
    }
  };

  const handleEmployeeChange = (selectedOption) => {
    setSelectedEmployeeOption(selectedOption);
    setForm({
      ...form,
      employee_Id: selectedOption ? selectedOption.value : null,
    });
  };

  const handleCustomerChange = (selectedOption) => {
    setSelectedCustomerOption(selectedOption);
    setForm({
      ...form,
      customer_Id: selectedOption ? selectedOption.value : null,
    });
  };

  const handleServiceChange = (selectedOption) => {
    setSelectedServiceOption(selectedOption);
    const serviceId = selectedOption ? selectedOption.value : null;
    const selectedService = selectedOption?.service || services.find(s => s.spaService_Id === serviceId);

    // Auto-calculate stop time based on service duration (duration is in minutes)
    if (selectedService && selectedService.duration && selectedService.duration > 0 && startTime && selectedDate) {
      const durationMinutes = selectedService.duration;
      // Create a new date using the selected date and start time
      const calculatedStopTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        startTime.getHours(),
        startTime.getMinutes(),
        startTime.getSeconds()
      );
      calculatedStopTime.setMinutes(calculatedStopTime.getMinutes() + durationMinutes);
      setStopTime(calculatedStopTime);
    }

    setForm({
      ...form,
      service_Id: serviceId,
      cost: selectedService ? (selectedService.cost || selectedService.Cost || 0) : 0,
      discount_Percent: 0,
      discount_Amount: 0,
    });
  };

  const handleDiscountPercentChange = (percent) => {
    const cost = form.cost || 0;
    const amount = (cost * (percent / 100));
    setForm({
      ...form,
      discount_Percent: percent,
      discount_Amount: parseFloat(amount.toFixed(2))
    });
  };

  const handleDiscountAmountChange = (amount) => {
    const cost = form.cost || 0;
    const percent = cost > 0 ? (amount / cost) * 100 : 0;
    setForm({
      ...form,
      discount_Amount: amount,
      discount_Percent: parseFloat(percent.toFixed(2))
    });
  };

  // Update datetime when date or times change (like AddBooking)
  useEffect(() => {
    if (selectedDate && startTime) {
      const combinedStartTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        startTime.getHours(),
        startTime.getMinutes(),
        startTime.getSeconds()
      );
      setForm(prevForm => ({
        ...prevForm,
        transaction_DateTime: formatLocalDateTime(combinedStartTime),
      }));
    }
  }, [selectedDate, startTime]);

  useEffect(() => {
    if (selectedDate && stopTime) {
      const combinedStopTime = new Date(
        selectedDate.getFullYear(),
        selectedDate.getMonth(),
        selectedDate.getDate(),
        stopTime.getHours(),
        stopTime.getMinutes(),
        stopTime.getSeconds()
      );
      setForm(prevForm => ({
        ...prevForm,
        end_DateTime: formatLocalDateTime(combinedStopTime),
      }));
    }
  }, [selectedDate, stopTime]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_Id || !form.branch_Id || !form.employee_Id || !form.customer_Id || !form.service_Id || !form.transaction_DateTime || !form.end_DateTime) {
      Swal.fire("Error", "Please fill all required fields: Company, Branch, Employee, Customer, Service, Start Time, and Stop Time", "error");
      return;
    }

    // Validate stop time is after start time
    if (new Date(form.end_DateTime) <= new Date(form.transaction_DateTime)) {
      Swal.fire("Error", "Stop time must be after start time", "error");
      return;
    }

    if (!form.cost || form.cost <= 0) {
      Swal.fire("Error", "Please enter a valid cost amount", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const sessionData = getUserSession();
      const daybookData = {
        daybook_Id: form.daybook_Id,
        business_Id: form.business_Id,
        branch_Id: form.branch_Id,
        employee_Id: form.employee_Id,
        customer_Id: form.customer_Id,
        service_Id: form.service_Id,
        transaction_DateTime: form.transaction_DateTime,
        end_DateTime: form.end_DateTime,
        cost: form.cost,
        discount_Percent: form.discount_Percent || 0,
        discount_Amount: form.discount_Amount || 0,
        notes: form.notes || null,
        created_By: sessionData.userId,
        created_Date: new Date().toISOString(),
      };

      const isEdit = form.daybook_Id > 0;
      const result = await saveDaybook(daybookData);
      Swal.fire("Success", result.message || "Daybook saved successfully", "success");

      if (isEdit) {
        setShowModal(false);
      } else {
        // Keep modal open and preserve Company, Branch, Employee, and Date
        // Only clear Customer, Service, Cost, Discount, and Notes
        setForm(prev => ({
          ...prev,
          daybook_Id: 0,
          customer_Id: null,
          service_Id: null,
          cost: 0,
          discount_Percent: 0,
          discount_Amount: 0,
          notes: "",
          end_DateTime: null,
        }));

        // Clear selected options for customer and service only
        setSelectedCustomerOption(null);
        setSelectedServiceOption(null);

        // Reset start and stop times to current time, but keep the date
        const now = new Date();
        const currentTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0);
        setStartTime(currentTime);
        setStopTime(currentTime);
      }

      // Refresh data
      setPage(1); // Reset to page 1 to see new records
      loadDaybooks(filter); // Refresh data
    } catch (error) {
      Swal.fire("Error", "Failed to save daybook: " + error.message, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadDaybooks(filter);
  };

  const handleClearFilter = () => {
    let newFilter = {
      ...emptyFilter,
      fromDate: null,
      toDate: null
    };

    setFilter(newFilter);
    setPage(1);
    loadDaybooks(newFilter);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString('en-GB');
  };

  const formatTimeOnly = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes.toString().padStart(2, '0');
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove null values and convert to proper types
      const cleanFilter = {
        ...(filter.employee_Id !== null && filter.employee_Id !== undefined && { employee_Id: filter.employee_Id }),
        ...(filter.service_Id !== null && filter.service_Id !== undefined && { service_Id: filter.service_Id }),
        ...(filter.business_Id !== null && filter.business_Id !== undefined && { business_Id: filter.business_Id }),
        ...(filter.branch_Id !== null && filter.branch_Id !== undefined && { branch_Id: filter.branch_Id }),
        ...(filter.customer_Id !== null && filter.customer_Id !== undefined && { customer_Id: filter.customer_Id }),
        ...(filter.booking_Id !== null && filter.booking_Id !== undefined && { booking_Id: filter.booking_Id }),
        ...(filter.fromDate && { fromDate: filter.fromDate }),
        ...(filter.toDate && { toDate: filter.toDate })
      };

      // For users without edit permission, filter by their employee ID only
      if (!hasEditOrHigher && currentEmployeeId) {
        cleanFilter.employee_Id = currentEmployeeId;
      }

      await exportDaybookToExcel(cleanFilter);
      Swal.fire("Success", "Daybook report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting daybook:", error);
      Swal.fire("Error", "Failed to export daybook report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    const now = new Date();

    setForm({
      ...defaultForm,
      business_Id: session.businessId || null,
      branch_Id: session.branchId || null,
    });

    // Set date and times (like AddBooking)
    setSelectedDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
    setStartTime(new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0));
    setStopTime(new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), now.getMinutes(), 0));

    // Reset search options
    setCustomerSearchOptions([]);
    setEmployeeSearchOptions([]);
    setServiceSearchOptions([]);
    setSelectedCustomerOption(null);
    setSelectedEmployeeOption(null);
    setSelectedServiceOption(null);

    // Load branches if business is auto-populated
    if (session.businessId) {
      loadBranches(session.businessId);
    }

    setShowModal(true);
  };

  const handleEdit = (daybook) => {
    setForm({
      ...daybook,
    });

    // Set date and times
    const transDate = new Date(daybook.transaction_DateTime);
    setSelectedDate(transDate);
    setStartTime(transDate);
    setStopTime(daybook.end_DateTime ? new Date(daybook.end_DateTime) : transDate);

    // Setup search options for the selected values
    if (daybook.customer_Id) {
      setSelectedCustomerOption({
        value: daybook.customer_Id,
        label: daybook.customer_Name || getCustomerDisplay(daybook.customer_Id)
      });
    }
    if (daybook.employee_Id) {
      setSelectedEmployeeOption({
        value: daybook.employee_Id,
        label: daybook.employee_Name || getEmployeeName(daybook.employee_Id)
      });
    }
    if (daybook.service_Id) {
      setSelectedServiceOption({
        value: daybook.service_Id,
        label: daybook.service_Name || getServiceName(daybook.service_Id)
      });
    }

    if (daybook.business_Id) {
      loadBranches(daybook.business_Id);
    }

    setShowModal(true);
  };

  const getBusinessName = (businessId) => {
    const business = businesses.find(b => b.business_Id === businessId);
    return business ? (business.business_NameEn || business.business_Name) : businessId;
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.branch_Id === branchId);
    return branch ? (branch.branch_NameEn || branch.branch_Name) : branchId;
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.employee_Id === employeeId);
    return employee ? employee.employee_NameEn : employeeId;
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.customer_Id === customerId);
    return customer ? customer.customer_NameEn : customerId;
  };

  const getCustomerPhone = (customerId) => {
    const customer = customers.find(c => c.customer_Id === customerId);
    return customer ? (customer.customer_ContactNo || '') : '';
  };

  const getCustomerDisplay = (customerId) => {
    const customer = customers.find(c => c.customer_Id === customerId);
    if (!customer) return customerId;
    const name = customer.customer_NameEn || 'N/A';
    const phone = customer.customer_ContactNo || '';
    return phone ? `${name} - ${phone}` : name;
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.spaService_Id === serviceId);
    return service ? (service.spaServiceName_En || service.SpaServiceName_En) : serviceId;
  };

  // Calculate total cost for current filtered data
  const calculateTotalCost = () => {
    return daybooks.reduce((total, daybook) => total + daybook.cost, 0);
  };

  if (!hasPermission) {
    return <div>Access Denied</div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Daybook Management" breadcrumbItem="Spa" />

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Daybook Management</h4>
          <div className="d-flex gap-2">
            {hasEditOrHigher && (
              <Button
                color="success"
                onClick={() => navigate('/GmsAddDaybook')}
                disabled={loading}
              >
                <i className="bx bx-plus-circle me-1"></i>
                Add Entry
              </Button>
            )}
            <Button
              color="primary"
              onClick={handleExportExcel}
              disabled={loading || totalCount === 0}
            >
              <Download className="me-1" size={16} />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Filter Section */}
        <Card className="mb-4">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Filter Daybook</h5>
              <Button
                color="primary"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="bx bx-filter me-2"></i>
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </div>
          </CardHeader>
          {showFilters && (
            <CardBody>
              <Row className="g-3 mb-3">
                <Col md={3}>
                  <label className="form-label">Company</label>
                  <Select
                    value={businesses.find(b => b.business_Id === filter.business_Id)
                      ? { value: filter.business_Id, label: businesses.find(b => b.business_Id === filter.business_Id).business_NameEn || businesses.find(b => b.business_Id === filter.business_Id).business_Name }
                      : null}
                    onChange={(option) => {
                      handleFilterChange('business_Id', option ? option.value : null);
                      if (!option) {
                        handleFilterChange('branch_Id', null);
                      }
                    }}
                    options={businesses.map(business => ({
                      value: business.business_Id,
                      label: business.business_NameEn || business.business_Name
                    }))}
                    isClearable
                    isSearchable
                    placeholder="Select Company..."
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">Branch</label>
                  <Select
                    value={branches.find(b => b.branch_Id === filter.branch_Id)
                      ? { value: filter.branch_Id, label: branches.find(b => b.branch_Id === filter.branch_Id).branch_NameEn || branches.find(b => b.branch_Id === filter.branch_Id).branch_Name }
                      : null}
                    onChange={(option) => handleFilterChange('branch_Id', option ? option.value : null)}
                    options={branches.filter(b => !filter.business_Id || b.branch_Buisness_Id === filter.business_Id).map(branch => ({
                      value: branch.branch_Id,
                      label: branch.branch_NameEn || branch.branch_Name
                    }))}
                    isClearable
                    isDisabled={!filter.business_Id}
                    isSearchable
                    placeholder={!filter.business_Id ? "Select Company first..." : "Select Branch..."}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                {showEmployeeFilter && (
                  <Col md={3}>
                    <label className="form-label">Employee</label>
                    <Select
                      value={filterEmployeeSearchOptions.find(e => e.value === filter.employee_Id) ||
                        (filter.employee_Id && employees.find(e => e.employee_Id === filter.employee_Id)
                          ? { value: filter.employee_Id, label: employees.find(e => e.employee_Id === filter.employee_Id).employee_NameEn }
                          : null)}
                      onChange={(option) => handleFilterChange('employee_Id', option ? option.value : null)}
                      options={filterEmployeeSearchOptions}
                      isClearable
                      isSearchable
                      isLoading={filterEmployeeSearchLoading}
                      noOptionsMessage={() => "Type at least 3 characters to search"}
                      loadingMessage={() => "Searching employees..."}
                      placeholder="Type at least 3 characters to search employees..."
                      onInputChange={(inputValue) => {
                        if (filterSearchTimeout) {
                          clearTimeout(filterSearchTimeout);
                        }
                        const timeout = setTimeout(() => {
                          searchFilterEmployeesManually(inputValue);
                        }, 300);
                        setFilterSearchTimeout(timeout);
                        return inputValue;
                      }}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: '#fff',
                          borderColor: '#ced4da',
                          color: '#495057'
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#495057'
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                          color: state.isSelected ? '#fff' : '#495057',
                          opacity: 1
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999
                        })
                      }}
                      maxMenuHeight={200}
                    />
                  </Col>
                )}
                <Col md={3}>
                  <label className="form-label">Customer</label>
                  <Select
                    value={filterCustomerSearchOptions.find(c => c.value === filter.customer_Id) ||
                      (filter.customer_Id && customers.find(c => c.customer_Id === filter.customer_Id)
                        ? { value: filter.customer_Id, label: customers.find(c => c.customer_Id === filter.customer_Id).customer_NameEn }
                        : null)}
                    onChange={(option) => handleFilterChange('customer_Id', option ? option.value : null)}
                    options={filterCustomerSearchOptions}
                    isClearable
                    isSearchable
                    isLoading={filterCustomerSearchLoading}
                    noOptionsMessage={() => "Type at least 3 characters to search"}
                    loadingMessage={() => "Searching customers..."}
                    placeholder="Type at least 3 characters to search customers..."
                    filterOption={() => true}
                    onInputChange={(inputValue) => {
                      if (filterSearchTimeout) {
                        clearTimeout(filterSearchTimeout);
                      }
                      const timeout = setTimeout(() => {
                        searchFilterCustomersManually(inputValue);
                      }, 300);
                      setFilterSearchTimeout(timeout);
                      return inputValue;
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">Service</label>
                  <Select
                    value={filterServiceSearchOptions.find(s => s.value === filter.service_Id) ||
                      (filter.service_Id && filteredServices.find(s => s.spaService_Id === filter.service_Id)
                        ? { value: filter.service_Id, label: filteredServices.find(s => s.spaService_Id === filter.service_Id).spaServiceName_En || filteredServices.find(s => s.spaService_Id === filter.service_Id).SpaServiceName_En }
                        : null)}
                    onChange={(option) => handleFilterChange('service_Id', option ? option.value : null)}
                    options={filterServiceSearchOptions}
                    isClearable
                    isSearchable
                    isLoading={filterServiceSearchLoading}
                    noOptionsMessage={() => "Type at least 3 characters to search"}
                    loadingMessage={() => "Searching services..."}
                    placeholder="Type at least 3 characters to search services..."
                    filterOption={() => true}
                    onInputChange={(inputValue) => {
                      if (filterSearchTimeout) {
                        clearTimeout(filterSearchTimeout);
                      }
                      const timeout = setTimeout(() => {
                        searchFilterServicesManually(inputValue);
                      }, 300);
                      setFilterSearchTimeout(timeout);
                      return inputValue;
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filter.fromDate || ''}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value || null)}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filter.toDate || ''}
                    onChange={(e) => handleFilterChange('toDate', e.target.value || null)}
                  />
                </Col>
              </Row>
              <div className="d-flex justify-content-end">
                <Button
                  color="primary"
                  className="me-2"
                  onClick={handleApplyFilter}
                  disabled={loading}
                >
                  <i className="bx bx-search me-2"></i>
                  Apply Filter
                </Button>
                <Button
                  color="warning"
                  onClick={handleClearFilter}
                >
                  <i className="bx bx-refresh me-2"></i>
                  Clear Filter
                </Button>
              </div>
            </CardBody>
          )}
        </Card>

        {/* Table Section */}
        <Card>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Daybook List ({totalCount})</h5>
              <div className="text-end">
                <div className="d-flex gap-3 justify-content-end align-items-center">
                  <div className="fw-bold text-primary" style={{ fontSize: '1rem' }}>
                    Gross: {totalCost.toFixed(2)}
                  </div>
                  <div className="fw-bold text-danger" style={{ fontSize: '1rem' }}>
                    Discount: {totalDiscount.toFixed(2)}
                  </div>
                  <div className="fw-bold text-success" style={{ fontSize: '1.2rem' }}>
                    Net Total: {totalNetCost.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div style={{
                overflowX: 'scroll',
                maxWidth: '100%',
                width: '100%',
                WebkitOverflowScrolling: 'touch'
              }}>
                <table className="table table-bordered table-nowrap dt-responsive nowrap dataTable no-footer dtr-inline" style={{
                  minWidth: '1200px',
                  width: 'max-content',
                  marginBottom: 0
                }}>
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Action</th>
                      <th>Employee</th>
                      <th>Customer</th>
                      <th>Service</th>
                      <th>Type</th>
                      <th>Date</th>
                      <th>Start Time</th>
                      <th>Stop Time</th>
                      <th>Cost</th>
                      <th>Disc %</th>
                      <th>Disc Amount</th>
                      <th>Net Cost</th>
                      <th>Company</th>
                      <th>Branch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {daybooks.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center">No daybook entries found</td>
                      </tr>
                    ) : (
                      daybooks.map((daybook, index) => {
                        const serviceName = getServiceName(daybook.service_Id);

                        return (
                          <tr key={daybook.daybook_Id}>
                            <td>{(page - 1) * pageSize + index + 1}</td>
                            <td>
                              <div className="d-flex gap-2">
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleEdit(daybook)}
                                  title="Edit Entry"
                                >
                                  <i className="bx bx-edit-alt"></i>
                                </Button>
                              </div>
                            </td>
                            <td>{daybook.employee_Name || getEmployeeName(daybook.employee_Id)}</td>
                            <td>
                              {daybook.customer_Id ? (
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedCustomerId(daybook.customer_Id);
                                    setShowCustomerProfile(true);
                                  }}
                                  style={{ color: '#556ee6', textDecoration: 'none', cursor: 'pointer' }}
                                >
                                  {daybook.customer_Name && daybook.customer_Phone
                                    ? `${daybook.customer_Name} - ${daybook.customer_Phone}`
                                    : daybook.customer_Name
                                      ? daybook.customer_Name
                                      : getCustomerDisplay(daybook.customer_Id)}
                                </a>
                              ) : '-'}
                            </td>
                            <td>{daybook.service_Name || serviceName}</td>
                            <td>{daybook.service_Type || 'Standalone Billed Service'}</td>
                            <td>{formatDate(daybook.transaction_DateTime)}</td>
                            <td>{formatTimeOnly(daybook.transaction_DateTime)}</td>
                            <td>{daybook.end_DateTime ? formatTimeOnly(daybook.end_DateTime) : '-'}</td>
                            <td>{daybook.cost.toFixed(2)}</td>
                            <td>{daybook.discount_Percent ? daybook.discount_Percent.toFixed(1) + '%' : '-'}</td>
                            <td>{daybook.discount_Amount ? daybook.discount_Amount.toFixed(2) : '-'}</td>
                            <td>{(daybook.cost - (daybook.discount_Amount || 0)).toFixed(2)}</td>
                            <td>{daybook.business_Name || (daybook.business_Id ? getBusinessName(daybook.business_Id) : '-')}</td>
                            <td>{daybook.branch_Name || (daybook.branch_Id ? getBranchName(daybook.branch_Id) : '-')}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-4 p-3"
              style={{ backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6' }}>
              <div className="text-muted" style={{ fontSize: '14px', fontWeight: '500' }}>
                <i className="mdi mdi-information-outline me-2" style={{ color: '#556ee6' }}></i>
                Showing <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{ color: '#556ee6', fontWeight: '600' }}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{ color: '#556ee6', fontWeight: '600' }}>{totalCount}</span> entries
              </div>
              {totalCount > pageSize && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                      color: page === 1 ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-left"></i>
                  </Button>

                  {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                    const pageNum = index + 1;
                    const isActive = page === pageNum;

                    return (
                      <Button
                        key={pageNum}
                        color={isActive ? "primary" : "light"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? '#556ee6' : '#fff',
                          borderColor: isActive ? '#556ee6' : '#dee2e6',
                          color: isActive ? '#fff' : '#495057',
                          fontWeight: isActive ? '600' : '400',
                          boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}

                  <Button
                    color="light"
                    size="sm"
                    disabled={page === Math.ceil(totalCount / pageSize)}
                    onClick={() => handlePageChange(page + 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                      color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Add Transaction Modal */}
        <Modal isOpen={showModal} toggle={() => {
          setShowModal(false);
          setPage(1); // Reset to page 1 to see new records
          loadDaybooks(filter); // Refresh data when modal closes
        }} size="lg">
          <ModalHeader toggle={() => {
            setShowModal(false);
          }}>
            {form.daybook_Id > 0 ? "Edit Daybook Transaction" : "Add Daybook Transaction"}
          </ModalHeader>
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label">Date *</label>
                  <DatePicker
                    selected={selectedDate}
                    onChange={(date) => setSelectedDate(date)}
                    className="form-control"
                    dateFormat="dd/MM/yyyy"
                    placeholderText="Select Date"
                    required
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Company *</label>
                  <Select
                    value={businesses.find(b => b.business_Id === form.business_Id)
                      ? { value: form.business_Id, label: businesses.find(b => b.business_Id === form.business_Id).business_NameEn || businesses.find(b => b.business_Id === form.business_Id).business_Name }
                      : null}
                    onChange={handleBusinessChange}
                    options={businesses.map(business => ({
                      value: business.business_Id,
                      label: business.business_NameEn || business.business_Name
                    }))}
                    isClearable={form.daybook_Id === 0}
                    isDisabled={form.daybook_Id > 0}
                    placeholder="Select Company..."
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Branch *</label>
                  <Select
                    value={branches.find(b => b.branch_Id === form.branch_Id)
                      ? { value: form.branch_Id, label: branches.find(b => b.branch_Id === form.branch_Id).branch_NameEn || branches.find(b => b.branch_Id === form.branch_Id).branch_Name }
                      : null}
                    onChange={handleBranchChange}
                    options={branches.filter(b => !form.business_Id || b.branch_Buisness_Id === form.business_Id).map(branch => ({
                      value: branch.branch_Id,
                      label: branch.branch_NameEn || branch.branch_Name
                    }))}
                    isClearable={form.daybook_Id === 0}
                    isDisabled={form.daybook_Id > 0 || !form.business_Id}
                    placeholder={!form.business_Id ? "Select Company first..." : "Select Branch..."}
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Employee *</label>
                  <div className="d-flex gap-2">
                    <div style={{ flex: 1 }}>
                      <Select
                        value={selectedEmployeeOption || (form.employee_Id && employees.find(e => e.employee_Id === form.employee_Id)
                          ? { value: form.employee_Id, label: employees.find(e => e.employee_Id === form.employee_Id).employee_NameEn }
                          : null)}
                        onChange={handleEmployeeChange}
                        options={employeeSearchOptions}
                        isClearable={form.daybook_Id === 0}
                        isDisabled={form.daybook_Id > 0}
                        isSearchable
                        isLoading={employeeSearchLoading}
                        noOptionsMessage={() => "Type at least 3 characters to search"}
                        loadingMessage={() => "Searching employees..."}
                        placeholder="Type at least 3 characters to search employees..."
                        onInputChange={(inputValue) => {
                          if (searchTimeout) {
                            clearTimeout(searchTimeout);
                          }
                          const timeout = setTimeout(() => {
                            searchEmployeesManually(inputValue);
                          }, 300);
                          setSearchTimeout(timeout);
                          return inputValue;
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (provided) => ({
                            ...provided,
                            backgroundColor: '#fff',
                            borderColor: '#ced4da',
                            color: '#495057'
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#495057'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                            color: state.isSelected ? '#fff' : '#495057',
                            opacity: 1
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999
                          })
                        }}
                        maxMenuHeight={200}
                      />
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <label className="form-label">Customer *</label>
                  <div className="d-flex gap-2">
                    <div style={{ flex: 1 }}>
                      <Select
                        value={selectedCustomerOption || (form.customer_Id && customers.find(c => c.customer_Id === form.customer_Id)
                          ? { value: form.customer_Id, label: customers.find(c => c.customer_Id === form.customer_Id).customer_NameEn }
                          : null)}
                        onChange={handleCustomerChange}
                        options={customerSearchOptions}
                        isClearable={form.daybook_Id === 0}
                        isDisabled={form.daybook_Id > 0}
                        isSearchable
                        isLoading={customerSearchLoading}
                        noOptionsMessage={() => "Type at least 3 characters to search"}
                        loadingMessage={() => "Searching customers..."}
                        placeholder="Type at least 3 characters to search customers..."
                        filterOption={() => true}
                        onInputChange={(inputValue) => {
                          if (searchTimeout) {
                            clearTimeout(searchTimeout);
                          }
                          const timeout = setTimeout(() => {
                            searchCustomersManually(inputValue);
                          }, 300);
                          setSearchTimeout(timeout);
                          return inputValue;
                        }}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (provided) => ({
                            ...provided,
                            backgroundColor: '#fff',
                            borderColor: '#ced4da',
                            color: '#495057'
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: '#495057'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                            color: state.isSelected ? '#fff' : '#495057',
                            opacity: 1
                          }),
                          menu: (provided) => ({
                            ...provided,
                            zIndex: 9999
                          })
                        }}
                        maxMenuHeight={200}
                      />
                    </div>
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => setShowAdvancedSearchModal(true)}
                      title="Advanced Customer Search"
                      style={{ height: '38px' }}
                      disabled={form.daybook_Id > 0}
                    >
                      <Search size={16} />
                    </Button>
                  </div>
                </Col>
                <Col md={6}>
                  <label className="form-label">Service *</label>
                  <Select
                    value={selectedServiceOption || (form.service_Id && filteredServices.find(s => s.spaService_Id === form.service_Id)
                      ? { value: form.service_Id, label: filteredServices.find(s => s.spaService_Id === form.service_Id).spaServiceName_En || filteredServices.find(s => s.spaService_Id === form.service_Id).SpaServiceName_En }
                      : null)}
                    onChange={handleServiceChange}
                    options={serviceSearchOptions}
                    isClearable={form.daybook_Id === 0}
                    isDisabled={form.daybook_Id > 0}
                    isSearchable
                    isLoading={serviceSearchLoading}
                    noOptionsMessage={() => "Type at least 3 characters to search"}
                    loadingMessage={() => "Searching services..."}
                    placeholder="Type at least 3 characters to search services..."
                    filterOption={() => true}
                    onInputChange={(inputValue) => {
                      if (searchTimeout) {
                        clearTimeout(searchTimeout);
                      }
                      const timeout = setTimeout(() => {
                        searchServicesManually(inputValue);
                      }, 300);
                      setSearchTimeout(timeout);
                      return inputValue;
                    }}
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Start Time *</label>
                  <DatePicker
                    selected={startTime}
                    onChange={(date) => setStartTime(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="form-control"
                    style={{ width: '100%' }}
                    required
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Stop Time *</label>
                  <DatePicker
                    selected={stopTime}
                    onChange={(date) => setStopTime(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="Time"
                    dateFormat="h:mm aa"
                    className="form-control"
                    style={{ width: '100%' }}
                    required
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Cost *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={form.cost}
                    onChange={(e) => handleFormChange('cost', parseFloat(e.target.value) || 0)}
                    required
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Discount %</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    className="form-control"
                    value={form.discount_Percent}
                    onChange={(e) => handleDiscountPercentChange(parseFloat(e.target.value) || 0)}
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Discount Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={form.discount_Amount}
                    onChange={(e) => handleDiscountAmountChange(parseFloat(e.target.value) || 0)}
                  />
                </Col>
                <Col md={4}>
                  <label className="form-label">Net Cost</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={(form.cost - (form.discount_Amount || 0)).toFixed(2)}
                    readOnly
                  />
                </Col>
                <Col md={12}>
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={form.notes || ''}
                    onChange={(e) => handleFormChange('notes', e.target.value)}
                    placeholder="Enter any additional notes..."
                  />
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button
                color="success"
                type="submit"
                disabled={saveLoading}
              >
                <i className="bx bx-check-circle me-2"></i>
                {saveLoading ? "Saving..." : (form.daybook_Id > 0 ? "Update Transaction" : "Save Transaction")}
              </Button>
              <Button
                color="secondary"
                onClick={() => {
                  setShowModal(false);
                  setPage(1); // Reset to page 1 to see new records
                  loadDaybooks(filter); // Refresh data when modal closes
                }}
              >
                <i className="bx bx-x-circle me-2"></i>
                Close
              </Button>
            </ModalFooter>
          </form>
        </Modal>

        {/* Advanced Customer Search Modal */}
        <CustomerAdvancedSearchModal
          isOpen={showAdvancedSearchModal}
          toggle={() => setShowAdvancedSearchModal(false)}
          userBranchId={session.branchId}
          onCustomerSelected={(customer) => {
            setForm(prev => ({
              ...prev,
              customer_Id: customer.customer_Id
            }));
            setSelectedCustomerOption({
              value: customer.customer_Id,
              label: `${customer.customer_NameEn || 'N/A'} — ${customer.customer_ContactNo || 'N/A'}`,
              customer: customer
            });
          }}
        />

        {/* Customer Profile Modal */}
        <CustomerProfileModal
          isOpen={showCustomerProfile}
          toggle={() => {
            setShowCustomerProfile(false);
            setSelectedCustomerId(null);
          }}
          customerId={selectedCustomerId}
        />
      </Container>
    </div>
  );
} 